/** Types and helpers of the order builder (mirror of apps/api/src/orders). */

export type OrderStatus = "draft" | "pending_approval" | "sent" | "signed" | "cancelled";
export type ExtraKind = "percent" | "included" | "on_request" | "fixed";
export type RoomKind = "custom" | "bathroom_small" | "bathroom_medium" | "bathroom_large";

export const ROOM_KIND_LABEL: Record<RoomKind, string> = {
  custom: "Custom room",
  bathroom_small: "Small bathroom · package",
  bathroom_medium: "Medium bathroom · package",
  bathroom_large: "Large bathroom · package",
};

export interface BathroomPackage {
  code: string;
  label: string;
  floor: number;
  wall: number;
}

/** Fixed price of a section for a package room, null for a custom room. */
export function packagePrice(packages: BathroomPackage[], kind: RoomKind, p: "floor" | "wall"): number | null {
  const code = kind === "bathroom_small" ? "small" : kind === "bathroom_medium" ? "medium" : kind === "bathroom_large" ? "large" : null;
  if (!code) return null;
  const pkg = packages.find((x) => x.code === code);
  return pkg ? Number(pkg[p]) : null;
}
export type Finish =
  | "spatolato"
  | "travertino"
  | "cemento_industriale"
  | "nuvolato"
  | "pietra_naturale"
  | "calce_antica"
  | "epoxy_chromatic"
  | "epoxy_metallic"
  | "epoxy_art";

export type System = "microcement" | "geopolymer" | "epoxy";
export const SYSTEMS: System[] = ["microcement", "geopolymer", "epoxy"];
export const SYSTEM_LABEL: Record<System, string> = { microcement: "Microcement", geopolymer: "Geopolymer", epoxy: "Epoxy" };
export const EPOXY_FINISHES: Array<{ finish: Finish; key: "chromatic" | "metallic" | "art"; label: string }> = [
  { finish: "epoxy_chromatic", key: "chromatic", label: "Chromatic epoxy" },
  { finish: "epoxy_metallic", key: "metallic", label: "Metallic epoxy" },
  { finish: "epoxy_art", key: "art", label: "Art epoxy" },
];
export const isEpoxyFinish = (f: Finish) => f.startsWith("epoxy_");

export interface OrderExtra {
  code: string;
  label: string;
  kind: ExtraKind;
  pct?: number;
  amount?: number;
}

export interface OrderSection {
  system: System;
  finish: Finish;
  colour?: string;
  sqm: number;
  unitPrice: number;
  extras: OrderExtra[];
  notes?: string;
}

export interface OrderRoomInput {
  /** Present once the order is saved; keeps the room's photos across saves. */
  id?: string;
  name: string;
  kind: RoomKind;
  floor?: OrderSection | null;
  wall?: OrderSection | null;
}

export interface OrderInput {
  customerName: string;
  company?: string;
  mobile: string;
  email?: string;
  emirate?: string;
  siteAddress: string;
  /** Site pin dropped on the map (WGS84); both or neither. */
  lat?: number | null;
  lng?: number | null;
  crmLeadId?: number | null;
  assigneeId?: string | null;
  rooms: OrderRoomInput[];
  bracketPct?: number | null;
  discountType?: "percent" | "fixed" | null;
  discountValue?: number | null;
  discountReason?: string;
  surveyDate?: string | null;
  installDate?: string | null;
  validUntil?: string;
  notes?: string;
  /** Internal note for the site team; never printed, copied onto the project. */
  teamNotes?: string;
}

export interface SectionTotals {
  base: number;
  extrasAmt: number;
  sold: boolean;
}

export interface OrderTotals {
  rooms: Array<{ floor: SectionTotals; wall: SectionTotals; total: number }>;
  customSqm: number;
  customSubtotal: number;
  floorBase: number;
  floorExtrasAmt: number;
  wallBase: number;
  wallExtrasAmt: number;
  subtotal: number;
  bracketPct: number;
  bracketAdj: number;
  gross: number;
  discountAmt: number;
  discountEffectivePct: number;
  net: number;
  vatAmt: number;
  total: number;
  depositAmt: number;
}

export interface OrderEvent {
  id: string;
  kind: string;
  actorId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface OrderRow {
  id: string;
  number: string;
  version: number;
  status: OrderStatus;
  customerName: string;
  company: string | null;
  mobile: string;
  total: number;
  createdAt: string;
  sentAt: string | null;
  signedAt: string | null;
  projectId: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
}

export interface Agent {
  id: string;
  name: string;
  role: "admin" | "sales";
}

export interface OrderPhoto {
  id: string;
  url: string;
  mimeType?: string | null;
  caption: string | null;
  createdAt: string;
  uploadedById: string | null;
}

export interface OrderRoomRow {
  id: string;
  photos: OrderPhoto[];
  position: number;
  name: string;
  kind: RoomKind;
  floorSystem: System;
  floorFinish: Finish | null;
  floorColour: string | null;
  floorSqm: number | null;
  floorUnitPrice: number | null;
  floorExtras: OrderExtra[];
  floorNotes: string | null;
  wallSystem: System;
  wallFinish: Finish | null;
  wallColour: string | null;
  wallSqm: number | null;
  wallUnitPrice: number | null;
  wallExtras: OrderExtra[];
  wallNotes: string | null;
  floorBase: number;
  floorExtrasAmt: number;
  wallBase: number;
  wallExtrasAmt: number;
}

export type PaymentKind = "deposit" | "balance" | "custom";
export type PaymentMethod = "stripe" | "bank_transfer" | "cash" | "card_pos";
export type PaymentStatus = "pending" | "paid" | "expired" | "cancelled";

export interface OrderPayment {
  id: string;
  kind: PaymentKind;
  method: PaymentMethod;
  amount: number;
  currency: string;
  status: PaymentStatus;
  checkoutUrl: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  note: string | null;
  createdAt: string;
  /** On the response of `request`: "sent" | "off" | "skipped" | "failed: …". */
  mailResult?: string;
}

export interface PaymentSummary {
  total: number;
  paid: number;
  pending: number;
  outstanding: number;
  depositDue: number;
  balanceDue: number;
}

export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = { deposit: "Deposit", balance: "Balance", custom: "Payment" };
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = { stripe: "Card (Stripe)", bank_transfer: "Bank transfer", cash: "Cash", card_pos: "Card (POS)" };

export type CrmInvoiceKind = "deposit" | "balance" | "full";
export const CRM_INVOICE_KIND_LABEL: Record<CrmInvoiceKind, string> = { deposit: "Deposit (Acconto)", balance: "Balance (Saldo)", full: "Full amount" };

/** Settings → Orders → Invoices in i-GlooCRM. */
export interface CrmInvoiceConfig {
  enabled: boolean;
  auto: boolean;
  bu: string;
  docType: string;
  agentId: number;
  assignedUserId: number;
  productId: number | null;
  currencyId: number;
  currencyRate: number;
}

export interface OrderCrmInvoice {
  id: string;
  kind: CrmInvoiceKind;
  crmInvoiceId: number;
  invoiceNo: string | null;
  amount: number;
  paid: boolean;
  createdAt: string;
}

export interface StripeView {
  configured: boolean;
  mode: "test" | "live" | null;
  keyLast4: string | null;
  webhookSet: boolean;
  webhookUrl: string;
}

export interface OrderFull extends OrderRow {
  supersedesId: string | null;
  createdById: string;
  approvedAt: string | null;
  email: string | null;
  emirate: string | null;
  siteAddress: string;
  lat: number | null;
  lng: number | null;
  crmLeadId: number | null;
  rooms: OrderRoomRow[];
  bracketPct: number;
  bracketAuto: boolean;
  discountType: "percent" | "fixed" | null;
  discountValue: number | null;
  discountReason: string | null;
  surveyDate: string | null;
  installDate: string | null;
  validUntil: string;
  notes: string | null;
  teamNotes: string | null;
  termsSnapshot: string | null;
  floorBase: number;
  floorExtrasAmt: number;
  wallBase: number;
  wallExtrasAmt: number;
  subtotal: number;
  bracketAdj: number;
  gross: number;
  discountAmt: number;
  net: number;
  vatPct: number;
  vatAmt: number;
  total: number;
  depositPct: number;
  depositAmt: number;
  pdfKey: string | null;
  renderedAt: string | null;
  signedPdfKey: string | null;
  publicToken: string;
  sentVia: string | null;
  signerName: string | null;
  idFrontKey: string | null;
  idBackKey: string | null;
  idDeleteAfter: string | null;
  cancelReason: string | null;
  project: { id: string; name: string } | null;
  payments: OrderPayment[];
  paymentSummary: PaymentSummary;
  crmAccountId: number | null;
  crmInvoices: OrderCrmInvoice[];
  crmInvoice: CrmInvoiceConfig;
  events: OrderEvent[];
  requiresApproval: boolean;
  editable: boolean;
  publicLink: string;
  /** Present on the response of `send`. */
  sendResult?: string;
  shareText?: string;
  link?: string;
}

export interface PriceRow {
  id: string;
  finish: Finish;
  label: string;
  floorPrice: number;
  wallPrice: number;
  active: boolean;
  sortOrder: number;
}

export interface ExtraOptionRow {
  id: string;
  code: string;
  label: string;
  kind: "percent" | "included" | "on_request";
  pct: number | null;
  active: boolean;
  sortOrder: number;
}

export interface OrderSettingsRow {
  vatPct: number;
  depositPct: number;
  approvalDiscountPct: number;
  validityDays: number;
  idRetentionDays: number;
  brackets: Array<{ maxSqm: number | null; pct: number }>;
  termsEn: string;
  companyName: string;
  companyLine2: string | null;
  companyLicence: string | null;
  companyTrn: string | null;
  companyAddress: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  companyWeb: string | null;
  paymentDetails: string | null;
  bathroomPackages: BathroomPackage[];
  geopolymerPct: number;
  epoxyPrices: { chromatic: number; metallic: number; art: number };
  stripe: StripeView;
  crmInvoice: CrmInvoiceConfig;
}

export interface OrderSettingsBundle {
  settings: OrderSettingsRow;
  priceList: PriceRow[];
  extraOptions: ExtraOptionRow[];
}

export interface CrmLead {
  leadId: number;
  name: string;
  firstName: string;
  lastName: string;
  company: string;
  mobile: string;
  email: string;
  city: string;
  source: string;
  status: string;
}

export interface PublicSection {
  finish: string;
  colour: string;
  sqm: number;
  unitPrice: number;
  packaged: boolean;
  extras: Array<{ label: string; value: string }>;
  total: number;
}

export interface PublicOrderView {
  number: string;
  status: OrderStatus;
  customerName: string;
  company: string | null;
  siteAddress: string;
  rooms: Array<{ name: string; kind: RoomKind; floor: PublicSection | null; wall: PublicSection | null; total: number }>;
  payments: Array<{ id: string; kind: PaymentKind; method: PaymentMethod; amount: number; status: PaymentStatus; url: string | null; paidAt: string | null }>;
  paymentSummary: PaymentSummary;
  bracketPct: number;
  discountAmt: number;
  discountLabel: string | null;
  net: number;
  vatPct: number;
  vatAmt: number;
  total: number;
  depositPct: number;
  depositAmt: number;
  terms: string | null;
  payment: string | null;
  validUntil: string;
  pdfUrl: string;
  signedAt: string | null;
  signerName: string | null;
  canSign: boolean;
  maskedMobile: string;
  hasEmail: boolean;
}

export interface OtpResult {
  channel: string;
  sentTo: string;
  result: string;
}

export interface SignInput {
  code: string;
  signerName: string;
  accepted: true;
  signaturePng: string;
}

// ---------------------------------------------------------------- helpers

export const FINISH_LABEL: Record<Finish, string> = {
  spatolato: "Spatolato Veneziano",
  nuvolato: "Nuvolato Artistico",
  cemento_industriale: "Cemento Industriale",
  calce_antica: "Calce Antica",
  pietra_naturale: "Pietra Naturale",
  travertino: "Travertino",
  epoxy_chromatic: "Chromatic epoxy",
  epoxy_metallic: "Metallic epoxy",
  epoxy_art: "Art epoxy",
};

/** List price per m² for a system + finish: microcement list, geopolymer list + %, epoxy flat rate by sub-type. */
export function listPriceFor(settings: Pick<OrderSettingsRow, "geopolymerPct" | "epoxyPrices">, priceList: PriceRow[], system: System, finish: Finish, p: "floor" | "wall"): number {
  if (system === "epoxy") {
    const e = EPOXY_FINISHES.find((x) => x.finish === finish);
    return e ? Number(settings.epoxyPrices[e.key] ?? 0) : 0;
  }
  const row = priceList.find((r) => r.finish === finish);
  const base = row ? Number(p === "floor" ? row.floorPrice : row.wallPrice) : 0;
  return system === "geopolymer" ? Math.round((base * (1 + Number(settings.geopolymerPct) / 100) + Number.EPSILON) * 100) / 100 : base;
}

export const EMIRATES = [
  "Dubai",
  "Abu Dhabi",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

/** The 29 Mediterranean colours, as on the website wizard. */
export const COLOURS = [
  "Bianco Corda", "Perla Antica", "Tortora", "Grigio Perla", "Nebbia Padana", "Cemento",
  "Grigio Ferro", "Ardesia", "Piombo", "Antracite", "Nero Carbone", "Terra Andalusa",
  "Rosa Argilla", "Terracotta", "Cotto Bruciato", "Ruggine", "Oxblood", "Ocra", "Senape",
  "Oliva", "Salvia", "Eucalipto", "Verde Bosco", "Azzurro Mediterraneo", "Verderame",
  "Blu Grecia", "Blu Petrolio", "Blu Notte", "Prugna",
];

export const STATUS_TONE: Record<OrderStatus, string> = {
  draft: "border-white/20 bg-white/5 text-ice/70",
  pending_approval: "border-gold/60 bg-gold/15 text-goldbright",
  sent: "border-ice/50 bg-ice/10 text-ice",
  signed: "border-green/60 bg-green/15 text-green",
  cancelled: "border-rose/60 bg-rose/20 text-pink",
};

export const STATUS_LABEL: Record<OrderStatus, string> = {
  draft: "Draft",
  pending_approval: "Awaiting approval",
  sent: "Sent",
  signed: "Signed",
  cancelled: "Cancelled",
};

export const aed = (n: number | null | undefined) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const pctLabel = (pct: number) =>
  pct === 100 ? "list" : pct > 100 ? `+${pct - 100}%` : `−${100 - pct}%`;

export const extraValue = (e: { kind: ExtraKind; pct?: number | null; amount?: number | null }) =>
  e.kind === "percent"
    ? `+${Number(e.pct ?? 0)}%`
    : e.kind === "fixed"
      ? `+${aed(e.amount)} AED`
      : e.kind === "included"
        ? "included"
        : "on request";

const day = (s: string | null | undefined) => (s ? s.slice(0, 10) : null);

/** An order as the editor edits it. */
export function toDraft(o: OrderFull): OrderInput {
  const section = (r: OrderRoomRow, p: "floor" | "wall"): OrderSection | null => {
    const finish = p === "floor" ? r.floorFinish : r.wallFinish;
    if (!finish) return null;
    return {
      system: (p === "floor" ? r.floorSystem : r.wallSystem) ?? "microcement",
      finish,
      colour: (p === "floor" ? r.floorColour : r.wallColour) ?? undefined,
      sqm: Number((p === "floor" ? r.floorSqm : r.wallSqm) ?? 0),
      unitPrice: Number((p === "floor" ? r.floorUnitPrice : r.wallUnitPrice) ?? 0),
      extras: (p === "floor" ? r.floorExtras : r.wallExtras) ?? [],
      notes: (p === "floor" ? r.floorNotes : r.wallNotes) ?? undefined,
    };
  };
  return {
    customerName: o.customerName,
    company: o.company ?? "",
    mobile: !o.mobile ? "" : o.mobile.startsWith("971") && o.mobile.length === 12 ? `+971 ${o.mobile.slice(3)}` : `+${o.mobile}`,
    email: o.email ?? "",
    emirate: o.emirate ?? "",
    siteAddress: o.siteAddress,
    lat: o.lat ?? null,
    lng: o.lng ?? null,
    crmLeadId: o.crmLeadId,
    assigneeId: o.assigneeId,
    rooms: o.rooms.map((r) => ({ id: r.id, name: r.name, kind: r.kind, floor: section(r, "floor"), wall: section(r, "wall") })),
    bracketPct: o.bracketAuto ? null : o.bracketPct,
    discountType: o.discountType,
    discountValue: o.discountValue,
    discountReason: o.discountReason ?? "",
    surveyDate: day(o.surveyDate),
    installDate: day(o.installDate),
    validUntil: day(o.validUntil) ?? undefined,
    notes: o.notes ?? "",
    teamNotes: o.teamNotes ?? "",
  };
}

/** What a draft still lacks before it can be sent or signed. */
export function missingToSend(o: { mobile: string; siteAddress: string; rooms: Array<{ floorFinish: string | null; wallFinish: string | null }> }): string[] {
  return [
    !o.mobile && "customer mobile",
    !o.siteAddress && "site address",
    !o.rooms.some((r) => r.floorFinish || r.wallFinish) && "a room with a floor or wall",
  ].filter((x): x is string => Boolean(x));
}

/** Drop empty strings so the API's optional validators stay happy. */
export function cleanDraft(d: OrderInput): OrderInput {
  const out: Record<string, unknown> = { ...d };
  for (const k of ["company", "email", "emirate", "discountReason", "notes", "teamNotes", "validUntil", "mobile", "siteAddress"] as const) {
    if (out[k] === "") delete out[k];
  }
  for (const k of ["surveyDate", "installDate"] as const) if (!out[k]) out[k] = null;
  if (!d.discountType || !d.discountValue) {
    out.discountType = null;
    out.discountValue = null;
  }
  out.rooms = d.rooms.map((r) => {
    const sec = (s: OrderSection | null | undefined) => (s ? { ...s, colour: s.colour || undefined, notes: s.notes || undefined } : null);
    return { ...(r.id ? { id: r.id } : {}), name: r.name.trim() || "Room", kind: r.kind, floor: sec(r.floor), wall: sec(r.wall) };
  });
  return out as unknown as OrderInput;
}
